/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo } from 'react';

import { css, jsx } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema';
import { editorCardProvider } from '@atlaskit/editor-card-provider';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import type { EditorProps } from '@atlaskit/editor-core/editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { useUniversalPreset } from '@atlaskit/editor-core/preset-universal';
import { usePreset } from '@atlaskit/editor-core/use-preset';

export type AgenticPlanEditorActions = Parameters<
	NonNullable<React.ComponentProps<typeof ComposableEditor>['onEditorReady']>
>[0];

type AgenticPlanEditorProps = {
	defaultValue: DocNode;
	onEditorReady: (editorActions: AgenticPlanEditorActions) => void;
};

// ComposableEditor `comment` appearance does not expose props for removing its chrome or aligning
// its internal toolbar/content padding with ReactRenderer. Keep these overrides scoped and minimal
// so edit mode can add the toolbar without horizontally shifting the plan text.
const editorWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- ComposableEditor does not expose props to remove the comment editor chrome.
	'& .akEditor': {
		backgroundColor: 'transparent',
		borderWidth: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Align the comment editor content area with the read-only ReactRenderer layout.
	'& .ak-editor-content-area': {
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- ComposableEditor's direct child wrappers add spacing that shifts edit mode away from the read view.
	'& .akEditor > div': {
		padding: 0,
		backgroundColor: 'transparent',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- ProseMirror sets this internally; overriding it keeps edit text aligned with renderer text.
	'& .ak-editor-content-area .ProseMirror.ProseMirror': {
		margin: 0,
	},
});

function AgenticPlanComposableEditor({ defaultValue, onEditorReady }: AgenticPlanEditorProps) {
	const editorProps: EditorProps = useMemo(
		() => ({
			appearance: 'comment',
			allowUndoRedoButtons: true,
			allowRule: true,
			allowTables: {
				advanced: true,
			},
			defaultValue,
			minHeight: 0,
			smartLinks: {
				allowBlockCards: true,
				allowEmbeds: false,
				provider: Promise.resolve(editorCardProvider),
			},
			textFormatting: {
				responsiveToolbarMenu: true,
			},
		}),
		[defaultValue],
	);

	/*  wires up all the standard editor plugins based on the editorProps */
	const universalPreset = useUniversalPreset({ props: editorProps });
	const { preset } = usePreset(() => universalPreset, [universalPreset]);

	const handleEditorReady = useCallback(
		(editorActions: AgenticPlanEditorActions) => {
			onEditorReady(editorActions);
		},
		[onEditorReady],
	);

	return (
		<div css={editorWrapperStyles}>
			<ComposableEditor
				{...editorProps}
				onEditorReady={handleEditorReady}
				preset={preset}
				shouldFocus
			/>
		</div>
	);
}

export function AgenticPlanEditor(props: AgenticPlanEditorProps): React.JSX.Element {
	return (
		<EditorContext>
			<AgenticPlanComposableEditor {...props} />
		</EditorContext>
	);
}
